import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicNav />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <p className="eyebrow mb-5">Legal</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] font-semibold text-zinc-900 mb-4">Terms of Service</h1>
          <p className="text-sm text-zinc-500 mb-10">Last updated: 28 August 2026</p>

          <div className="max-w-none space-y-12 text-zinc-700 text-[15px] leading-7">
            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">1. Acceptance of these terms</h2>
              <p>
                These Terms of Service ("Terms") govern your use of Club Pass, a service operated by Airsoft
                Ventures Limited ("Club Pass", "we", "us", "our"). By creating an account or using the service,
                you agree to these Terms on behalf of yourself and, if applicable, the organisation you represent.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">2. The service</h2>
              <p>
                Club Pass lets organisations design and issue digital membership passes to their members via
                Google Wallet, and manage their member records. Access is provided on a subscription basis as
                described on our <a href="/pricing" className="font-medium text-zinc-900 underline underline-offset-4 decoration-zinc-300 hover:decoration-zinc-900">Pricing</a> page,
                plus a free, non-paid trial with limited functionality.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">3. Accounts</h2>
              <p>
                You must sign in with a Google account to use Club Pass. You are responsible for maintaining the
                security of your account and for all activity that occurs under it. You must provide accurate
                information about your organisation.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">4. Authorization to issue Wallet passes on your behalf</h2>
              <p>
                Club Pass operates under a single Google Wallet issuer account shared across all customer
                organisations, in line with Google's own supported setup model for platforms like ours. By using
                the service, you authorise Airsoft Ventures Limited to create, update, and manage Google Wallet
                pass classes and objects on your organisation's behalf, using the content (logos, colours, member
                details) you provide, solely for the purpose of issuing your organisation's membership passes.
                You confirm you have the necessary rights to submit any content (including member photos) you
                upload for this purpose.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">5. Subscriptions and billing</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Paid plans are billed annually in advance, in New Zealand dollars, via Stripe.</li>
                <li>Prices shown are GST-exclusive.</li>
                <li>You may change plans at any time; upgrades and downgrades are charged or credited the prorated difference immediately.</li>
                <li>You may cancel at any time via the Stripe Customer Portal; access continues until the end of the current paid term unless otherwise stated.</li>
                <li>The free trial does not require payment details and is limited to a small number of test passes, which are watermarked.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">6. Acceptable use</h2>
              <p className="mb-2">You agree not to use Club Pass to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Issue passes containing false, misleading, or unauthorised content.</li>
                <li>Upload personal information of members without appropriate authority to do so.</li>
                <li>Attempt to circumvent plan limits, trial restrictions, or the security of the service.</li>
                <li>Use the service for any unlawful purpose.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">7. Your data</h2>
              <p>
                Your use of Club Pass is also governed by our{" "}
                <a href="/privacy" className="font-medium text-zinc-900 underline underline-offset-4 decoration-zinc-300 hover:decoration-zinc-900">Privacy Policy</a>, which describes
                how we collect and handle personal information, including your members' information.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">8. Disclaimer and limitation of liability</h2>
              <p>
                Club Pass is provided "as is" without warranties of any kind, to the maximum extent permitted by
                law. We are not liable for indirect, incidental, or consequential damages arising from your use of
                the service, including issues arising from third-party services we rely on (Google Wallet, Google
                OAuth, Stripe, Resend). Nothing in these Terms limits any rights you have under the New Zealand
                Consumer Guarantees Act that cannot lawfully be excluded.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">9. Termination</h2>
              <p>
                You may stop using Club Pass and cancel your subscription at any time. We may suspend or terminate
                accounts that breach these Terms, or that we reasonably believe pose a security or legal risk to
                the service or other customers.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">10. Governing law</h2>
              <p>These Terms are governed by the laws of New Zealand.</p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">11. Changes to these terms</h2>
              <p>
                We may update these Terms from time to time. We will update the "Last updated" date above when we
                do, and material changes will be communicated to account owners directly.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">12. Contact us</h2>
              <p>
                Questions about these Terms can be sent to{" "}
                <a href="mailto:info@airsoftsystems.co.nz" className="font-medium text-zinc-900 underline underline-offset-4 decoration-zinc-300 hover:decoration-zinc-900">
                  info@airsoftsystems.co.nz
                </a>.
              </p>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default TermsOfServicePage;
